import React, { useContext, useEffect, useRef, useState } from "react";
import S from "/util/Sanctuary";
import * as R from "ramda";
import { push } from "connected-react-router";
import { useNavigate } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { ThemeContext } from "styled-components";
import {
  Placeholder,
  Box,
  Center,
  Stack,
  Loading,
  Timeout,
  Title,
  CenterStack,
  Heading,
  Paragraph,
  Detail,
  withWindowSize,
  constants,
  util,
  NotFoundIcon
} from "@thecb/components";
import BillingAddressSlider from "../profile-set-up-autopay/BillingAddressSlider";
import ConfirmAutopaySlider from "../profile-set-up-autopay/ConfirmAutopaySlider";
import PaymentCaptureSlider from "../profile-set-up-autopay/PaymentCaptureSlider";
import { Obligation } from "/components/obligation";
import AlertBar from "/components/alert-bar";
import AutopayUnavailableAlert, {
  isAutopayUnavailable
} from "../../AutopayUnavailableAlert";
import { isAutoPayEnabled, retrieveAutoPayData } from "/util/general";
import { FONT_WEIGHT_SEMIBOLD } from "../../../../../../constants/style_constants";
import {
  checkIsInCart,
  createCartConfig,
  createLineItems,
  getNextAutopayDate as getDueDate,
  actionsForAssociation,
  getCreditCardsForDisplay,
  getBankAccountsForDisplay,
  getTurnstileSiteKey,
  getTurnstileVerifyURL
} from "../../Profile.selectors";
import {
  DEACTIVATE_SCHEDULE_SETTING,
  CREDIT_CARD_RESOURCE,
  ACH_RESOURCE,
  PAYMENT_SCHEDULES_RESOURCE,
  CREDIT_CARD_KIND,
  BANK_ACCOUNT_KIND
} from "../../Profile.state";
import {
  ACH_METHOD,
  CASH_METHOD,
  CC_METHOD
} from "../../../../../../constants/data_constants";
import { isInCustomerManagement } from "../../../../../../util/router-utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { INLINE_AUTOPAY_FLAG } from "/util/launchDarklyUtils";

const ProfileAccounts = ({
  resourcesActions,
  alertBars,
  actions,
  forms,
  autoPayData,
  obligationAssociations,
  canAddObligation,
  resources,
  schedulesLoad,
  requests,
  onLogout,
  cartEnabled,
  paymentConfigs,
  carts,
  detailedObligation,
  termsAndConditionsAgreedTo,
  selectedAutopaymentMethodId,
  inlineAutopayOrchestration
}) => {
  let navigate = useNavigate();
  const ldFlags = useFlags();
  const INLINE_AUTOPAY_ENABLED = ldFlags[INLINE_AUTOPAY_FLAG];
  const {
    createPaymentFromProfile,
    setDetailedObligation,
    deactivatePaymentSchedule,
    navigateToDetailedObligation,
    fetchObligations,
    deleteObligationAssoc,
    configureMultiCart,
    addToMultiCart,
    openCartSlider,
    toggleTermsAndConditions,
    clearAutoPayInfo,
    createPaymentSchedule,
    setAutoPaymentSelectedPaymentMethod,
    createAutopayWithInlineData,
    resetInlineAutopayOrchestration,
    inlineAutopayStepUpdate
  } = resourcesActions;
  const oblActions = {
    createPaymentFromProfile,
    setDetailedObligation,
    navigateToDetailedObligation,
    deleteObligationAssoc,
    configureMultiCart,
    addToMultiCart,
    openCartSlider
  };
  const { isMobile } = useContext(ThemeContext);
  const {
    achAccounts,
    addresses,
    creditCards,
    obligations,
    schedules
  } = resources;

  const getNextAutopayDate = getDueDate(schedules);
  const autoPayUnavailable = isAutopayUnavailable(schedulesLoad);
  const deactivateScheduleRequest =
    requests[DEACTIVATE_SCHEDULE_SETTING] || S.RemoteData.NotAsked;

  const hasPaymentMethod = R.or(
    R.not(R.isEmpty(achAccounts ?? {})),
    R.not(R.isEmpty(creditCards ?? {}))
  );
  const autoPayAvailable =
    hasPaymentMethod && R.not(R.isEmpty(addresses ?? {}));
  const addressMissing = R.isEmpty(addresses ?? {});
  const siteKey = useSelector(getTurnstileSiteKey);
  const verifyURL = useSelector(getTurnstileVerifyURL);

  const [billingAddressModalOpen, setBillingAddressModalOpen] = useState(false);
  const [confirmAutopayModalOpen, setConfirmAutopayModalOpen] = useState(false);
  const [paymentCaptureModalOpen, setPaymentCaptureModalOpen] = useState(false);
  const [showAddressErrors, setShowAddressErrors] = useState(false);
  const [showPaymentErrors, setShowPaymentErrors] = useState(false);
  const [pendingAddress, setPendingAddress] = useState(null);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(null);
  const [selectedObligation, setSelectedObligation] = useState(null);
  const [wentThroughBillingAddress, setWentThroughBillingAddress] = useState(
    false
  );
  const creditCardRequest =
    requests[CREDIT_CARD_RESOURCE] || S.RemoteData.NotAsked;
  const achRequest = requests[ACH_RESOURCE] || S.RemoteData.NotAsked;
  const scheduleRequest =
    requests[PAYMENT_SCHEDULES_RESOURCE] || S.RemoteData.NotAsked;
  const isAddingPayment = creditCardRequest.isLoading || achRequest.isLoading;
  const pendingPaymentInfoRef = useRef(null);
  const { cardSaveSucceeded, addressSaveSucceeded } =
    inlineAutopayOrchestration || {};

  // Advance only once the card save is confirmed, not on click.
  useEffect(() => {
    if (!cardSaveSucceeded) return;
    setPaymentCaptureModalOpen(false);
    setShowPaymentErrors(false);
    if (pendingPaymentInfoRef.current) {
      setPendingPaymentMethod(pendingPaymentInfoRef.current);
      pendingPaymentInfoRef.current = null;
    }
    if (addressMissing) {
      setBillingAddressModalOpen(true);
      setWentThroughBillingAddress(true);
    } else {
      setConfirmAutopayModalOpen(true);
      setWentThroughBillingAddress(false);
    }
  }, [cardSaveSucceeded]);

  // Advance only once the address save is confirmed, not on click.
  useEffect(() => {
    if (!addressSaveSucceeded) return;
    setBillingAddressModalOpen(false);
    setShowAddressErrors(false);
    setConfirmAutopayModalOpen(true);
  }, [addressSaveSucceeded]);

  const allPaymentMethods = [
    ...getCreditCardsForDisplay(resources),
    ...getBankAccountsForDisplay(resources)
  ];
  const firstPaymentMethod = allPaymentMethods[0] ?? null;
  const selectedPaymentMethod =
    allPaymentMethods.find(
      method => method.id === selectedAutopaymentMethodId
    ) ?? firstPaymentMethod;

  // Terms differ by payment kind, so switching methods requires re-agreeing.
  const handlePaymentMethodChange = id => {
    setAutoPaymentSelectedPaymentMethod(id);
    if (termsAndConditionsAgreedTo) {
      toggleTermsAndConditions();
    }
  };

  useEffect(() => {
    if (confirmAutopayModalOpen && firstPaymentMethod) {
      setAutoPaymentSelectedPaymentMethod(firstPaymentMethod.id);
    }
  }, [confirmAutopayModalOpen]);

  // The slug lives on config, not on the obligation itself.
  const obligationSlug = selectedObligation?.config?.obligationSlug;

  const handleAddAddress = util.general.generateClickHandler(
    forms.addressForm,
    () => setShowAddressErrors(true),
    () => {
      const f = forms.addressForm.fields;
      setPendingAddress({
        street1: f.street1?.rawValue ?? "",
        street2: f.street2?.rawValue || null,
        city: f.city?.rawValue ?? "",
        stateProvince: f.stateProvince?.rawValue ?? "",
        zip: f.zip?.rawValue ?? "",
        country: f.country?.rawValue ?? "US"
      });
      createAutopayWithInlineData({
        obligationSlug,
        inlineAddressData: {}
      });
    }
  );

  const displayAddress = pendingAddress;

  const handleConfirmAutopay = () => {
    if (obligationSlug && hasPaymentMethod && !addressMissing) {
      inlineAutopayStepUpdate("schedule", "attempted");
      createPaymentSchedule({ obligationSlug });
    }
    setConfirmAutopayModalOpen(false);
    setPendingAddress(null);
    setPendingPaymentMethod(null);
    clearAutoPayInfo();
    // Otherwise these stay true in Redux and re-pop this slider on a later mount.
    resetInlineAutopayOrchestration();
  };

  const selectedAllowedPaymentInstruments = selectedObligation?.obligations?.[0]
    ?.allowedPaymentInstruments ?? [CASH_METHOD, CC_METHOD, ACH_METHOD];

  const handleAddCreditCard = util.general.generateClickHandler(
    forms.addCreditCardForm,
    () => setShowPaymentErrors(true),
    () => {
      const cardNumber = forms.addCreditCardForm.fields.creditCardNumber.rawValue.replace(
        /\s/g,
        ""
      );
      pendingPaymentInfoRef.current = {
        kind: CREDIT_CARD_KIND,
        lastFour: cardNumber.slice(-4),
        cardType: util.general.checkCardBrand(cardNumber)
      };
      createAutopayWithInlineData({
        obligationSlug,
        inlineCardData: { kind: CREDIT_CARD_KIND }
      });
    }
  );

  const handleAddACH = util.general.generateClickHandler(
    forms.addAchForm,
    () => setShowPaymentErrors(true),
    () => {
      const accountNumber = forms.addAchForm.fields.accountNumber.rawValue;
      pendingPaymentInfoRef.current = {
        kind: BANK_ACCOUNT_KIND,
        lastFour: accountNumber.slice(-4),
        accountType: forms.addAchForm.fields.accountType?.rawValue || "CHECKING"
      };
      createAutopayWithInlineData({
        obligationSlug,
        inlineCardData: { kind: BANK_ACCOUNT_KIND }
      });
    }
  );

  const handleAutopayClick = (association, obligations) => {
    resetInlineAutopayOrchestration();
    clearAutoPayInfo();
    setPendingAddress(null);
    setPendingPaymentMethod(null);
    setWentThroughBillingAddress(false);
    setSelectedObligation({
      name: obligations[0]?.description ?? "",
      config: association.config,
      obligations
    });
    // createPaymentScheduleEpic reads obligationAssociationId from here.
    setDetailedObligation(obligations, association.config, association.id);
    if (!hasPaymentMethod) {
      setPaymentCaptureModalOpen(true);
    } else if (addressMissing) {
      setBillingAddressModalOpen(true);
    } else {
      setConfirmAutopayModalOpen(true);
    }
  };
  const obligationsFetched =
    (obligations?.isSuccess ?? false) || (obligations?.isFailure ?? false);
  useEffect(() => {
    if (!obligationsFetched) {
      fetchObligations();
    }
  }, [obligationsFetched]);

  const activeAccounts = Object.entries(
    obligationAssociations?.accounts?.active ?? {}
  ).map(([activeGroupName, associations]) => {
    const accountObligations = associations.map(association => {
      const lineItems = createLineItems(association);
      const cartConfig = createCartConfig(
        association,
        lineItems,
        paymentConfigs
      );
      const isInCart = checkIsInCart(carts, association);
      const associationActions = actionsForAssociation(oblActions, association);

      if (association.config.collection) {
        return (
          <Obligation
            key={`${activeGroupName}-${association.id}-collection`}
            config={association.config}
            obligations={association.obligations}
            actions={associationActions}
            autoPayEnabled={isAutoPayEnabled(autoPayData, association.id)}
            autoPayAvailable={INLINE_AUTOPAY_ENABLED ? true : autoPayAvailable}
            autoPayUnavailable={autoPayUnavailable}
            autoPaySchedule={
              retrieveAutoPayData(autoPayData, association.id).id
            }
            handleAutopayAction={
              INLINE_AUTOPAY_ENABLED
                ? () => handleAutopayClick(association, association.obligations)
                : () => navigate("set-up-autopay")
            }
            navigateToSettings={() => navigate("/profile/wallet")}
            deactivatePaymentSchedule={deactivatePaymentSchedule}
            deactivatingSchedule={deactivateScheduleRequest?.isLoading}
            isMobile={isMobile}
            nextAutopayDate={getNextAutopayDate(association.id)}
            obligationAssocID={association.id}
            dueDate={association.dueDate}
            agencyName={activeGroupName}
            inactive={!!association.error}
            inactiveLookupTitle={
              association?.config?.inactiveLookupTitle ?? activeGroupName
            }
            inactiveLookupInput={association?.config?.inactiveLookupInput}
            inactiveLookupValue={
              association?.config?.inactiveLookupValue ?? association.account
            }
            inactiveCanNavigateToAccountDetails
            isInCustomerManagement={isInCustomerManagement}
            cartEnabled={cartEnabled}
            cartConfig={cartConfig}
            isInCart={isInCart}
            isLoading={association.isLoading}
            showRemoveButton={!!association.error}
          />
        );
      }
      return association.obligations?.map(obl => (
        <Obligation
          key={`${activeGroupName}-${association.id}-${obl.id}`}
          config={association.config}
          obligations={[obl]}
          actions={associationActions}
          autoPayEnabled={isAutoPayEnabled(autoPayData, association.id)}
          autoPayAvailable={INLINE_AUTOPAY_ENABLED ? true : autoPayAvailable}
          autoPayUnavailable={autoPayUnavailable}
          autoPaySchedule={retrieveAutoPayData(autoPayData, association.id).id}
          handleAutopayAction={
            INLINE_AUTOPAY_ENABLED
              ? () => handleAutopayClick(association, [obl])
              : () => navigate("set-up-autopay")
          }
          navigateToSettings={() => navigate("/profile/wallet")}
          deactivatePaymentSchedule={deactivatePaymentSchedule}
          deactivatingSchedule={deactivateScheduleRequest?.isLoading}
          isMobile={isMobile}
          nextAutopayDate={getNextAutopayDate(association.id)}
          obligationAssocID={association.id}
          dueDate={association.dueDate}
          agencyName={activeGroupName}
          inactive={!!association.error}
          inactiveLookupTitle={
            association?.config?.inactiveLookupTitle ?? activeGroupName
          }
          inactiveLookupInput={association?.config?.inactiveLookupInput}
          inactiveLookupValue={
            association?.config?.inactiveLookupValue ?? association.account
          }
          inactiveCanNavigateToAccountDetails
          isInCustomerManagement={isInCustomerManagement}
          cartEnabled={cartEnabled}
          cartConfig={cartConfig}
          isInCart={isInCart}
          isLoading={association.isLoading}
          showRemoveButton={!!association.error}
        />
      ));
    });
    return (
      <Stack childGap="0.5rem" key={`active-accounts-${activeGroupName}`}>
        <Box padding="0">
          <Detail as="h2" variant="regular">
            {activeGroupName}
          </Detail>
        </Box>
        <Box padding="0">
          <Stack childGap="24px">
            {accountObligations}
            {/* Hide ability to add new account if in customer management */}
            {canAddObligation && (
              <Placeholder
                isLink
                isDisabled={isInCustomerManagement}
                text="Add more accounts"
                destination="/profile/find"
                dataQa="add account"
              />
            )}
          </Stack>
        </Box>
      </Stack>
    );
  });
  const inactiveAccounts = Object.entries(
    obligationAssociations?.accounts?.inactive ?? {}
  ).map(([inactiveGroupName, associations]) => {
    const accountObligations = associations.map(association => {
      const associationActions = actionsForAssociation(oblActions, association);
      if (association.config.collection) {
        return (
          <Obligation
            key={`${inactiveGroupName}-${association.id}-collection`}
            config={association.config}
            obligations={association.obligations}
            actions={associationActions}
            autoPayEnabled={isAutoPayEnabled(autoPayData, association.id)}
            autoPayAvailable={INLINE_AUTOPAY_ENABLED ? true : autoPayAvailable}
            autoPayUnavailable={autoPayUnavailable}
            autoPaySchedule={
              retrieveAutoPayData(autoPayData, association.id).id
            }
            handleAutopayAction={
              INLINE_AUTOPAY_ENABLED
                ? () => handleAutopayClick(association, association.obligations)
                : () => navigate("set-up-autopay")
            }
            navigateToSettings={() => navigate("/profile/wallet")}
            deactivatePaymentSchedule={deactivatePaymentSchedule}
            deactivatingSchedule={deactivateScheduleRequest?.isLoading}
            isMobile={isMobile}
            nextAutopayDate={getNextAutopayDate(association.id)}
            obligationAssocID={association.id}
            dueDate={association.dueDate}
            agencyName={inactiveGroupName}
            inactive
            inactiveLookupTitle={
              association?.config?.inactiveLookupTitle ?? inactiveGroupName
            }
            inactiveLookupInput={association?.config?.inactiveLookupInput}
            inactiveLookupValue={
              association?.config?.inactiveLookupValue ?? association.account
            }
            inactiveCanNavigateToAccountDetails
            isInCustomerManagement={isInCustomerManagement}
            isLoading={association.isLoading}
            showRemoveButton={false}
          />
        );
      }
      return association.obligations.map(obl => (
        <Obligation
          key={`${inactiveGroupName}-${association.id}-${obl.id}`}
          config={association.config}
          obligations={[obl]}
          actions={associationActions}
          autoPayEnabled={isAutoPayEnabled(autoPayData, association.id)}
          autoPayAvailable={INLINE_AUTOPAY_ENABLED ? true : autoPayAvailable}
          autoPayUnavailable={autoPayUnavailable}
          autoPaySchedule={retrieveAutoPayData(autoPayData, association.id).id}
          handleAutopayAction={
            INLINE_AUTOPAY_ENABLED
              ? () => handleAutopayClick(association, [obl])
              : () => navigate("set-up-autopay")
          }
          navigateToSettings={() => navigate("/profile/wallet")}
          deactivatePaymentSchedule={deactivatePaymentSchedule}
          deactivatingSchedule={deactivateScheduleRequest?.isLoading}
          isMobile={isMobile}
          nextAutopayDate={getNextAutopayDate(association.id)}
          obligationAssocID={association.id}
          dueDate={association.dueDate}
          agencyName={inactiveGroupName}
          inactive
          inactiveLookupTitle={
            association?.config?.inactiveLookupTitle ?? inactiveGroupName
          }
          inactiveLookupInput={association?.config?.inactiveLookupInput}
          inactiveLookupValue={
            association?.config?.inactiveLookupValue ?? association.account
          }
          inactiveCanNavigateToAccountDetails
          isInCustomerManagement={isInCustomerManagement}
          isLoading={obl.isLoading}
          showRemoveButton={false}
        />
      ));
    });
    return (
      <Stack childGap="0.5rem" key={`inactive-accounts-${inactiveGroupName}`}>
        <Box padding="0">
          <Detail as="h2" variant="regular">
            {inactiveGroupName}
          </Detail>
        </Box>
        <Box padding="0">
          <Stack childGap="1.5rem">{accountObligations}</Stack>
        </Box>
      </Stack>
    );
  });

  const {
    accountsAlertBar: alertBar,
    addCreditCardAlertBar: alertBarCC,
    addAchAlertBar: alertBarACH,
    addressAlertBar
  } = alertBars;
  const {
    alertBars: {
      accountsAlertBar: alertBarActions,
      addCreditCardAlertBar: alertBarCCActions,
      addAchAlertBar: alertBarACHActions,
      addressAlertBar: addressAlertBarActions
    }
  } = actions;

  // Page-level bar so this stays visible after any slider closes.
  useEffect(() => {
    if (!scheduleRequest.isFailure) return;
    alertBarActions.replaceAlerts({
      heading: "Autopay enrollment failed. Please try again.",
      variant: "error"
    });
  }, [scheduleRequest.isFailure]);

  const getStatus = obligations =>
    obligations.isLoading
      ? "loading"
      : obligations.isSuccess
      ? "success"
      : obligations.isFailure && obligations.error === "TIMEOUT"
      ? "timeout"
      : "not found";

  const renderObligations = obligations => {
    const status = getStatus(obligations);
    switch (status) {
      case "loading":
        return <Loading />;
      case "timeout":
        return <Timeout onLogout={onLogout} />;
      case "success":
        return (
          <Box padding="0" as="section">
            {R.isEmpty(activeAccounts) && (
              <Box
                background={constants.colors.WHITE}
                extraStyles={`box-shadow: 0px 2px 14px 0px ${constants.colors.ATHENS_GREY},
      0px 3px 8px 0px ${constants.colors.GHOST_GREY}; margin-bottom: 16px;`}
                key="placeholder-box"
              >
                <Placeholder
                  isLink
                  variant="large"
                  isDisabled={isInCustomerManagement}
                  destination="/profile/find"
                  text="Find an Account"
                  largeIcon="accounts"
                  key="placeholder"
                  dataQa="add account"
                />
              </Box>
            )}
            {R.or(!R.isEmpty(activeAccounts), !R.isEmpty(inactiveAccounts)) && (
              <Stack childGap="16px" key="account-stack">
                {!R.isEmpty(activeAccounts) && (
                  <Box
                    padding="0"
                    as="section"
                    aria-labelledby="active-accounts-section-heading"
                  >
                    <Stack childGap="0">
                      <Box padding="0">
                        <Title
                          variant="small"
                          as="h1"
                          weight={FONT_WEIGHT_SEMIBOLD}
                          id="active-accounts-section-heading"
                          dataQa="active-accounts-section-heading"
                        >
                          Active Accounts
                        </Title>
                      </Box>
                      <Box padding="0">
                        <Stack childGap="24px">{activeAccounts}</Stack>
                      </Box>
                    </Stack>
                  </Box>
                )}
                {!R.isEmpty(inactiveAccounts) && (
                  <Box
                    padding="0"
                    as="section"
                    aria-labelledby="inactive-accounts-section-heading"
                    extraStyles={`padding-top: 16px;`}
                  >
                    <Stack childGap="0">
                      <Box padding="0">
                        <Title
                          variant="small"
                          as="h2"
                          weight={FONT_WEIGHT_SEMIBOLD}
                          id="inactive-accounts-section-heading"
                          dataQa="inactive-accounts-section-heading"
                        >
                          Inactive Accounts
                        </Title>
                      </Box>
                      <Box padding="0">
                        <Stack childGap="24px">{inactiveAccounts}</Stack>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        );
      default:
        return (
          <CenterStack
            content={
              <Box padding={isMobile ? "1rem" : "2rem"}>
                <Box
                  padding={isMobile ? "1rem 0 2rem" : "1rem"}
                  extraStyles={isMobile && `svg { max-width: 100%; }`}
                >
                  <Center intrinsic>
                    <NotFoundIcon
                      style={isMobile ? { height: "158px", width: "auto" } : {}}
                    />
                  </Center>
                </Box>
                <Heading variant="h4" textAlign="center" weight="bold">
                  Error finding your accounts
                </Heading>
                <Stack>
                  <Center singleChild>
                    <Box padding="0.5rem 0 0" maxWidth="37.5rem">
                      <Paragraph extraStyles="text-align: center;">
                        {`Looks like we’re currently unable to find your accounts. Please refresh the page or try again later.`}
                      </Paragraph>
                    </Box>
                  </Center>
                </Stack>
              </Box>
            }
            contentSpacing="0"
            maxContentWidth="100%"
          />
        );
    }
  };

  return (
    <Box padding="0 0 16px" key="accounts-box" role="main">
      <Stack fullHeight childGap="24px" key="accounts-outer-stack">
        {!R.isEmpty(alertBar.alerts) && (
          <AlertBar {...alertBar} {...alertBarActions} key="alert-bar" />
        )}
        <AutopayUnavailableAlert
          schedulesLoad={schedulesLoad}
          onRetry={resourcesActions.fetchSchedules}
        />
        {renderObligations(obligations)}
      </Stack>
      {INLINE_AUTOPAY_ENABLED && (
        <>
          <PaymentCaptureSlider
            isOpen={paymentCaptureModalOpen}
            isMobile={isMobile}
            onClose={() => {
              setPaymentCaptureModalOpen(false);
              setShowPaymentErrors(false);
              pendingPaymentInfoRef.current = null;
              setPendingPaymentMethod(null);
              resetInlineAutopayOrchestration();
              actions.forms.addCreditCardForm.actions.form.clear();
              actions.forms.addAchForm.actions.form.clear();
              actions.forms.captchaForm.actions.form.clear();
            }}
            onAddCreditCard={handleAddCreditCard}
            onAddACH={handleAddACH}
            allowedPaymentInstruments={selectedAllowedPaymentInstruments}
            addCreditCardForm={forms.addCreditCardForm}
            addCreditCardFormActions={actions.forms.addCreditCardForm}
            addAchForm={forms.addAchForm}
            addAchFormActions={actions.forms.addAchForm}
            captchaFormActions={actions.forms.captchaForm}
            siteKey={siteKey}
            verifyURL={verifyURL}
            showErrors={showPaymentErrors}
            detailedObligation={selectedObligation}
            addCreditCardAlertBar={alertBarCC}
            addCreditCardAlertBarActions={alertBarCCActions}
            addAchAlertBar={alertBarACH}
            addAchAlertBarActions={alertBarACHActions}
            isAddingPayment={isAddingPayment}
          />
          <BillingAddressSlider
            isOpen={billingAddressModalOpen}
            isMobile={isMobile}
            onClose={() => {
              setBillingAddressModalOpen(false);
              setShowAddressErrors(false);
              setPendingPaymentMethod(null);
              resetInlineAutopayOrchestration();
              actions.forms.addressForm.actions.form.clear();
            }}
            onAddAddress={handleAddAddress}
            addressForm={forms.addressForm}
            addressFormActions={actions.forms.addressForm}
            showErrors={showAddressErrors}
            addressAlertBar={addressAlertBar}
            addressAlertBarActions={addressAlertBarActions}
            paymentMethod={pendingPaymentMethod}
            paymentMethodJustAdded={pendingPaymentMethod !== null}
          />
          <ConfirmAutopaySlider
            isOpen={confirmAutopayModalOpen}
            isMobile={isMobile}
            onClose={() => {
              setConfirmAutopayModalOpen(false);
              setPendingAddress(null);
              setPendingPaymentMethod(null);
              setWentThroughBillingAddress(false);
              resetInlineAutopayOrchestration();
              clearAutoPayInfo();
            }}
            onConfirm={handleConfirmAutopay}
            paymentMethod={pendingPaymentMethod ?? selectedPaymentMethod}
            availablePaymentMethods={allPaymentMethods}
            selectedAutopaymentMethodId={selectedAutopaymentMethodId}
            onPaymentMethodChange={handlePaymentMethodChange}
            address={displayAddress}
            detailedObligation={selectedObligation}
            paymentConfigs={paymentConfigs}
            termsAgreed={termsAndConditionsAgreedTo}
            onToggleTerms={() => toggleTermsAndConditions(false)}
            addressJustAdded={pendingAddress !== null}
            paymentMethodJustAdded={
              pendingPaymentMethod !== null && !wentThroughBillingAddress
            }
          />
        </>
      )}
    </Box>
  );
};

export default connect(null, { push })(
  withWindowSize(React.memo(ProfileAccounts))
);
